/**
 * Package Actions
 *
 * Actions for Oracle PL/SQL packages, surfaced in the command palette
 * and other object-action contexts.
 */

import { FolderOpen } from 'lucide-react';
import type { ObjectActionDefinition, ObjectActionContext } from '../types';
import { useWorkspaceStore } from '../../../workspace/core/store/workspaceStore';
import { getConnectionById } from '../../../connections/utils/connectionLookup';
import { createDefaultPlSqlEditorContent } from '../../../workspace/plsql-editor/types';
import { findExistingEditorTab } from '../../../workspace/pl-sql-debugger/hooks/debugHelpers';
import { safeConnectionAlias, safeConnectionServer } from '../../../../types/safeConnectionDisplay';

/**
 * Open package in PL/SQL Editor action
 */
export const openPackageAction: ObjectActionDefinition = {
  id: 'open-package',
  label: 'Open in PL/SQL Editor',
  icon: FolderOpen,
  category: 'query',
  appliesTo: ['package'],
  isPrimary: true,
  execute: (context: ObjectActionContext) => {
    const { activeConnection, connection } = getConnectionById(context.connectionId);
    if (!activeConnection || !connection) return;

    const { tabs, addTab, setActiveTab } = useWorkspaceStore.getState();
    const existingId = findExistingEditorTab(tabs, context.schemaName, context.objectName, context.connectionId);
    if (existingId) {
      setActiveTab(existingId);
      return;
    }

    addTab({
      title: `${context.schemaName}.${context.objectName}`,
      type: 'plsql-editor',
      content: createDefaultPlSqlEditorContent(
        context.objectName,
        context.schemaName,
        context.databaseName,
        'package',
      ),
      connectionInfo: {
        connectionId: context.connectionId,
        connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
        serverHost: safeConnectionServer(activeConnection.connection),
        database: context.databaseName,
      },
    });
  },
};

/**
 * All package actions
 */
export const packageActions: ObjectActionDefinition[] = [
  openPackageAction,
];
